/**
 * Pure math for StringCursor's per-object cursor tracking: converting
 * pixel positions to a normalized offset (and back), and adjusting a base
 * lerp factor for the actual frame delta so it converges at the same
 * perceived speed regardless of framerate.
 */
export class CursorOffsetCalculator {
  static calculateOffset(alignment: string, mousePos: number, size: number): number {
    switch (alignment) {
      case "start":
        return mousePos / size;
      case "end":
        return (mousePos - size) / size;
      case "center":
      default:
        return (mousePos - size / 2) / (size / 2);
    }
  }

  static reverseOffset(alignment: string, offset: number, size: number): number {
    switch (alignment) {
      case "start":
        return offset * size;
      case "end":
        return offset * size + size;
      case "center":
      default:
        return offset * (size / 2) + size / 2;
    }
  }

  private static readonly MIN_FRAME_DELTA = 1 / 240;

  static getFrameAdjustedLerp(base: number, dt: number): number {
    const clampedBase = Math.min(0.99, Math.max(0.001, base));
    if (!Number.isFinite(dt) || dt <= 0) {
      return clampedBase;
    }

    const normalizedDt = Math.max(dt, CursorOffsetCalculator.MIN_FRAME_DELTA);
    const frames = normalizedDt * 60;
    const adjusted = 1 - Math.pow(1 - clampedBase, frames);
    return Math.min(0.999, Math.max(0.0001, adjusted));
  }
}
